// useSettingsPlanPaneViewModel.js

import { useMemo } from 'react';

// Utils
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';
import { computeEffectiveLimitsFromPlan } from 'services/bases/BaseEntitlementsService';

import {
    buildBaseThresholdAlerts,
    formatBillingPeriodEndLabel,
    formatBillingStatus,
    formatBytes,
    formatDateTime,
    formatMonthLabel,
    formatPlanBillingMonthlyTotalText,
    formatPlanPrice,
    formatSubscriptionCancelScheduledMessage,
    formatUsageValue,
    formatWholeCount,
    resolvePlanStorageBytesPerSeat,
    secondsToWholeMinutes,
} from '../utils/settingsPlanPaneUtils';

/**
 * useSettingsPlanPaneViewModel
 *
 * Builds the display-oriented summary state for SettingsPlanPane.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.apiLimits - Base API usage limits snapshot.
 * @param {object} params.billingRecord - Base billing record.
 * @param {boolean} params.billingLoading - Whether the billing record is loading.
 * @param {object} params.currentPlan - Active space plan row.
 * @param {boolean} params.isCurrentPlanHiddenFromCatalog - Whether the active plan is hidden from the public catalog.
 * @param {number} [params.billedSeatCount] - Billed seat count including pending invites.
 * @param {object} params.liveUsage - Base live usage snapshot.
 * @param {number} [params.pendingInviteCount] - Pending space invite count.
 * @param {object} params.base - Active base model.
 *
 * @returns {object} Derived plan summary and usage view state.
 */
const useSettingsPlanPaneViewModel = ({
    apiLimits,
    billedSeatCount: billedSeatCountOverride,
    billingLoading,
    billingRecord,
    currentPlan,
    entitlementsError = null,
    entitlementsLoading = false,
    isCurrentPlanHiddenFromCatalog,
    liveUsage,
    pendingInviteCount = 0,
    usageAllowance = null,
    base,
}) => {
    const currentPlanPriceText = useMemo(() => {
        return formatPlanPrice(currentPlan);
    }, [currentPlan]);

    const billingStatusText = useMemo(() => {
        return billingLoading ? 'Loading...' : formatBillingStatus(billingRecord?.billingStatus);
    }, [billingLoading, billingRecord?.billingStatus]);

    const billingRenewsText = useMemo(() => {
        return billingLoading ? 'Loading...' : formatDateTime(billingRecord?.currentPeriodEnd);
    }, [billingLoading, billingRecord?.currentPeriodEnd]);

    const billingPeriodEndLabel = useMemo(() => {
        return formatBillingPeriodEndLabel(billingRecord);
    }, [billingRecord]);

    const billingCancellationNotice = useMemo(() => {
        if (billingLoading) {
            return '';
        }

        return formatSubscriptionCancelScheduledMessage({
            billingRecord,
            planTitle: currentPlan?.title,
        });
    }, [billingLoading, billingRecord, currentPlan?.title]);

    const billingMonthlyTotalText = useMemo(() => {
        if (billingLoading) {
            return null;
        }

        return formatPlanBillingMonthlyTotalText({
            plan: currentPlan,
            seatCount: billingRecord?.seatCount,
        });
    }, [billingLoading, billingRecord?.seatCount, currentPlan]);

    const billedSeatCount = useMemo(() => {
        const override = Number(billedSeatCountOverride);
        if (Number.isFinite(override) && override > 0) {
            return Math.max(1, Math.floor(override));
        }

        return Math.max(1, Math.floor(Number(base?.memberCount || 1)));
    }, [billedSeatCountOverride, base?.memberCount]);

    const effectivePlanLimits = useMemo(() => {
        return computeEffectiveLimitsFromPlan({
            billedSeatCount,
            plan: currentPlan,
        });
    }, [billedSeatCount, currentPlan]);

    const membersUsageText = useMemo(() => {
        const baseMemberCount = Math.max(0, Math.floor(Number(base?.memberCount || 0)));
        const pricingModel = String(currentPlan?.pricingModel || '').trim();

        if (pricingModel === 'per_seat') {
            const normalizedPendingInviteCount = Math.max(0, Math.floor(Number(pendingInviteCount || 0)));
            let summary = `${formatWholeCount(baseMemberCount)} active • ${formatWholeCount(billedSeatCount)} billed seat${billedSeatCount === 1 ? '' : 's'}`;

            if (normalizedPendingInviteCount > 0) {
                summary += ` • ${formatWholeCount(normalizedPendingInviteCount)} pending invite${normalizedPendingInviteCount === 1 ? '' : 's'}`;
            }

            return summary;
        }

        const currentPlanUserLimit = Number(currentPlan?.maxUsers || 0);
        if (currentPlanUserLimit > 0) {
            return `${formatWholeCount(baseMemberCount)} / ${formatWholeCount(currentPlanUserLimit)}`;
        }

        return `${formatWholeCount(baseMemberCount)} active`;
    }, [billedSeatCount, currentPlan?.maxUsers, currentPlan?.pricingModel, pendingInviteCount, base?.memberCount]);

    const storageUsageState = useMemo(() => {
        const effectiveStorageBytes = Math.max(0, Math.floor(Number(effectivePlanLimits.limits.storageBytes || 0)));
        const storageBytesPerSeat = resolvePlanStorageBytesPerSeat(currentPlan);
        const storageBytesUsed = Math.max(0, Math.floor(Number(base?.storageBytesUsed || 0)));
        const storageBytesReserved = Math.max(0, Math.floor(Number(base?.storageBytesReserved || 0)));
        const storageBytesEffective = storageBytesUsed + storageBytesReserved;

        const storageUsageText = effectiveStorageBytes > 0
            ? `${formatBytes(storageBytesEffective)} / ${formatBytes(effectiveStorageBytes)}`
            : `${formatBytes(storageBytesEffective)} used`;

        const storageReservedNote = storageBytesReserved > 0
            ? `${formatBytes(storageBytesReserved)} is currently reserved for in-flight uploads.`
            : '';

        const storagePerSeatNote = storageBytesPerSeat
            ? `${formatBytes(storageBytesPerSeat)} per seat × ${formatWholeCount(billedSeatCount)} seat${billedSeatCount === 1 ? '' : 's'}`
            : '';

        return {
            storagePerSeatNote,
            storageReservedNote,
            storageUsageText,
        };
    }, [
        billedSeatCount,
        currentPlan,
        effectivePlanLimits.limits.storageBytes,
        base?.storageBytesReserved,
        base?.storageBytesUsed,
    ]);

    const shouldShowApiUsage = Boolean(currentPlan?.apiEnabled || isCurrentPlanHiddenFromCatalog);
    // Live usage cards gated with the product surface (admin/plan fields remain).
    const shouldShowLiveUsage = LIVE_ROOMS_SURFACE_ENABLED
        && Boolean(currentPlan?.liveEnabled || isCurrentPlanHiddenFromCatalog);
    const shouldShowRecordingUsage = LIVE_ROOMS_SURFACE_ENABLED && Boolean(
        (currentPlan?.liveEnabled && currentPlan?.liveRecordingEnabled)
        || isCurrentPlanHiddenFromCatalog
    );

    const apiUsageSummary = useMemo(() => {
        const monthlyRuns = apiLimits?.usage?.apiMonthlyRuns || null;
        const requestCount = Math.max(0, Math.floor(Number(monthlyRuns?.requestCount || 0)));
        const readCount = Math.max(0, Math.floor(Number(monthlyRuns?.readCount || 0)));
        const writeCount = Math.max(0, Math.floor(Number(monthlyRuns?.writeCount || 0)));
        const limit = Math.max(0, Math.floor(Number(
            monthlyRuns?.limit
            || effectivePlanLimits.limits.apiMonthlyRunLimit
            || currentPlan?.apiMonthlyRunLimitPerSeat
            || currentPlan?.apiMonthlyRunLimit
            || 0
        )));

        return {
            detailText: `${formatWholeCount(readCount)} reads • ${formatWholeCount(writeCount)} writes`,
            limit,
            monthLabel: formatMonthLabel(monthlyRuns?.monthKey),
            requestCount,
            summaryText: formatUsageValue({
                limit,
                used: requestCount,
            }),
        };
    }, [apiLimits, currentPlan?.apiMonthlyRunLimit, currentPlan?.apiMonthlyRunLimitPerSeat, effectivePlanLimits.limits.apiMonthlyRunLimit]);

    const liveUsageSummary = useMemo(() => {
        const participantMinutesUsed = secondsToWholeMinutes(liveUsage?.participantSeconds || 0);
        const recordingMinutesUsed = secondsToWholeMinutes(liveUsage?.egressSeconds || 0);
        const participantMinutesCap = Math.max(0, Math.floor(Number(
            effectivePlanLimits.limits.liveParticipantMinutesMonthlyCap
            || currentPlan?.liveParticipantMinutesPerSeat
            || currentPlan?.liveParticipantMinutesMonthlyCap
            || 0
        )));
        const recordingMinutesCap = Math.max(0, Math.floor(Number(
            effectivePlanLimits.limits.liveEgressMinutesMonthlyCap
            || currentPlan?.liveEgressMinutesPerSeat
            || currentPlan?.liveEgressMinutesMonthlyCap
            || 0
        )));
        const activeRoomCount = Math.max(0, Math.floor(Number(liveUsage?.activeRoomCount || 0)));
        const activeParticipantCount = Math.max(0, Math.floor(Number(liveUsage?.activeParticipantCount || 0)));

        return {
            activeNowText: `${formatWholeCount(activeRoomCount)} room${activeRoomCount === 1 ? '' : 's'} • ${formatWholeCount(activeParticipantCount)} participants`,
            monthLabel: formatMonthLabel(liveUsage?.monthKey),
            participantMinutesCap,
            participantMinutesText: formatUsageValue({
                limit: participantMinutesCap,
                used: participantMinutesUsed,
            }),
            participantMinutesUsed,
            recordingMinutesCap,
            recordingMinutesText: formatUsageValue({
                limit: recordingMinutesCap,
                used: recordingMinutesUsed,
            }),
        };
    }, [
        currentPlan?.liveEgressMinutesMonthlyCap,
        currentPlan?.liveEgressMinutesPerSeat,
        currentPlan?.liveParticipantMinutesMonthlyCap,
        currentPlan?.liveParticipantMinutesPerSeat,
        effectivePlanLimits.limits.liveEgressMinutesMonthlyCap,
        effectivePlanLimits.limits.liveParticipantMinutesMonthlyCap,
        liveUsage,
    ]);

    const thresholdAlerts = useMemo(() => {
        const storageBytesUsed = Math.max(0, Math.floor(Number(base?.storageBytesUsed || 0)));
        const storageBytesReserved = Math.max(0, Math.floor(Number(base?.storageBytesReserved || 0)));

        return buildBaseThresholdAlerts({
            apiRequestCount: apiUsageSummary.requestCount,
            apiRunLimit: apiUsageSummary.limit,
            liveParticipantMinutesCap: LIVE_ROOMS_SURFACE_ENABLED
                ? liveUsageSummary.participantMinutesCap
                : 0,
            liveParticipantMinutesUsed: LIVE_ROOMS_SURFACE_ENABLED
                ? liveUsageSummary.participantMinutesUsed
                : 0,
            storageBytesEffective: storageBytesUsed + storageBytesReserved,
            storageBytesLimit: effectivePlanLimits.limits.storageBytes,
            teamUsagePercentUsed: usageAllowance?.percentUsed,
        });
    }, [
        apiUsageSummary.limit,
        apiUsageSummary.requestCount,
        effectivePlanLimits.limits.storageBytes,
        liveUsageSummary.participantMinutesCap,
        liveUsageSummary.participantMinutesUsed,
        usageAllowance?.percentUsed,
        base?.storageBytesReserved,
        base?.storageBytesUsed,
    ]);

    const usageAllowanceSummary = useMemo(() => {
        const creditsEffective = Math.max(0, Math.floor(Number(usageAllowance?.creditsEffective || 0)));
        const creditsUsed = Math.max(0, Math.floor(Number(usageAllowance?.creditsUsed || 0)));
        const shouldShow = creditsEffective > 0 || creditsUsed > 0;

        if (!shouldShow) {
            return {
                percent: null,
                shouldShow: false,
                text: '',
            };
        }

        if (entitlementsLoading) {
            return {
                percent: null,
                shouldShow: true,
                text: 'Loading...',
            };
        }

        if (entitlementsError) {
            return {
                percent: null,
                shouldShow: true,
                text: 'Unavailable',
            };
        }

        const percent = creditsEffective > 0
            ? Math.min(100, Math.round((creditsUsed / creditsEffective) * 100))
            : (creditsUsed > 0 ? 100 : 0);

        return {
            percent,
            shouldShow: true,
            text: `${percent}% of included allowance (${creditsUsed.toLocaleString()} / ${creditsEffective.toLocaleString()} credits)`,
        };
    }, [
        entitlementsError,
        entitlementsLoading,
        usageAllowance?.creditsEffective,
        usageAllowance?.creditsUsed,
    ]);

    return {
        apiUsageSummary,
        billingCancellationNotice,
        billingMonthlyTotalText,
        billingPeriodEndLabel,
        billingRenewsText,
        billingStatusText,
        currentPlanPriceText,
        liveUsageSummary,
        membersUsageText,
        shouldShowApiUsage,
        shouldShowLiveUsage,
        shouldShowRecordingUsage,
        storagePerSeatNote: storageUsageState.storagePerSeatNote,
        storageReservedNote: storageUsageState.storageReservedNote,
        storageUsageText: storageUsageState.storageUsageText,
        thresholdAlerts,
        usageAllowanceSummary,
    };
};

export default useSettingsPlanPaneViewModel;